import { Button, Tooltip } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  supportedMetricsSelector,
  getFirstAppliedFilter,
  intervalSelector,
} from '@store/common/selectors';
import {
  allDimensionsListSelector,
  dimensionsListSelectorKeyValue,
} from '@store/common/dimensionList/selectors';
import { createTempDashboard } from '@store/tempDashboard/actions';
import { isAppModule } from '@utils/constants';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { selector as summaryContainerSelector } from '@store/summaryContainer';
import { createFilterRule, checkIfRuleAlreadyExist } from '@utils/filter';
import AnalysisIcon from '@icons/analysis.svg';
import {
  getAppEventsPreset,
  appPresetKeys,
  appPresetTitles,
  getServerSidePerformancePreset,
} from '../common/Dashboard/UseCases/configs/app';
import {
  getValidateDimensionFunc,
  getValidateMetricFunc,
} from '../common/Dashboard/UseCases/utils';
import { drilldownDashboardType } from '../common/Dashboard/UseCases';
import { networkRequestUrlDimensions } from './utils/constant';

const getPresetConfigFunc = (target: 'appEvents' | 'serverSidePerformance') => {
  return {
    appEvents: getAppEventsPreset,
    serverSidePerformance: getServerSidePerformancePreset,
  }[target];
};
const getDrillDownIcon = (): JSX.Element => {
  return <AnalysisIcon fill="white" />;
};
/**
 * Drill down to perset Event Scope, take along with current global filter and dimension filter with current value.
 */
export const RCADrillDownToEventScope: React.FC<{ rowData: object }> = ({ rowData }) => {
  const metrics = useSelector(supportedMetricsSelector) as object;
  const dimensions = useSelector(dimensionsListSelectorKeyValue) as object;
  const dimensionList = useSelector(allDimensionsListSelector);
  const globalFilter = useSelector(getFirstAppliedFilter);
  const formattedInterval = useSelector(intervalSelector);
  const summaryContainer = useSelector(summaryContainerSelector);
  const dispatch = useDispatch();
  const dimension = useMemo(() => Object.values(networkRequestUrlDimensions), []);
  const targetPreset = 'serverSidePerformance';

  const handleActions = useCallback(() => {
    let drilldownFilters = [...summaryContainer.drilldown];
    let newFilter = cloneDeep(globalFilter);
    const combinedFilter = [...globalFilter.rule];
    const crumbRulesList: any[] = [];
    dimension.forEach((d) => {
      const dimensionInfo = (dimensionList as any).find((it) => it.groupBy === d);
      const { name: dimensionName } = dimensionInfo;
      if (!rowData[d]) {
        return;
      }
      const filterByValue = createFilterRule(dimensionList, dimensionName, rowData[d], rowData[d]);

      /** The logic of handling drilldown and globalFilter is referred from
       *  function dimensionDrillown to ensure the structure is consistent. */
      const doesRuleExist = checkIfRuleAlreadyExist(globalFilter.rule, filterByValue);
      if (doesRuleExist) {
        return;
      }
      crumbRulesList.push([filterByValue]);
      combinedFilter.push([filterByValue]);
    });
    const crumbRules = getRulesFromFilter(crumbRulesList, true);
    const crumbFilter = getFilterFromRules(crumbRules, true);
    if (drilldownFilters.length === 0) {
      drilldownFilters.push(cloneDeep(globalFilter));
    }
    drilldownFilters = [...drilldownFilters, crumbFilter];
    const newRules = getRulesFromFilter(combinedFilter, true);
    newFilter = getFilterFromRules(newRules, true);

    const getPresetConfig = getPresetConfigFunc(targetPreset);
    if (!getPresetConfig) {
      return;
    }
    const { config, title, key } = getPresetConfig({
      validateDimension: getValidateDimensionFunc(dimensions),
      validateMetric: getValidateMetricFunc(metrics),
    });
    dispatch(
      createTempDashboard(
        drilldownDashboardType,
        {
          ...config,
          common: {
            appliedFilters: [newFilter],
            interval: formattedInterval,
          },
          summary: {
            drilldownData: drilldownFilters,
          },
          usecaseKey: key,
        },
        title,
      ),
    );
  }, [
    summaryContainer.drilldown,
    globalFilter,
    dimension,
    dimensions,
    metrics,
    dispatch,
    formattedInterval,
    dimensionList,
    rowData,
  ]);

  if (!isAppModule) {
    return null;
  }
  return (
    <Tooltip title={`Drill down to ${appPresetTitles[appPresetKeys.APP_NETWORK_PERFORMANCE]}`}>
      <Button
        type="text"
        style={{
          backgroundColor: '#0046ff',
          color: 'white',
          width: '40px',
        }}
        icon={getDrillDownIcon()}
        onClick={handleActions}
      />
    </Tooltip>
  );
};
